import { useEffect, useState } from "react";
import { Archive, Plus, RotateCcw, Trash2, Users, UsersRound, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import JaceLayout from "./JaceLayout";
import { JACE } from "./shared";

function GroupForm({ group, athletes, onClose, onSaved }) {
  const [name, setName] = useState(group?.name || "");
  const [description, setDescription] = useState(group?.description || "");
  const [selected, setSelected] = useState(group?.athlete_ids || []);
  const [busy, setBusy] = useState(false);

  const toggle = (id) => setSelected((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Groepsnaam is verplicht"); return; }
    setBusy(true);
    try {
      const payload = { name: name.trim(), description, athlete_ids: selected };
      const { data } = group ? await api.put(`/jace/groups/${group.id}`, payload) : await api.post("/jace/groups", payload);
      toast.success(group ? "Groep bijgewerkt" : "Groep aangemaakt");
      onSaved(data.group || data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Opslaan mislukt");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-xl w-full p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="jace-group-form">
        <div className="flex items-center justify-between mb-5">
          <div className="chip" style={{ color: JACE }}>// {group ? "GROEP BEWERKEN" : "NIEUWE GROEP"}</div>
          <button onClick={onClose} className="chip text-[#A3A3A3]" data-testid="jace-group-form-close"><X size={14}/></button>
        </div>
        <div className="space-y-4">
          <div><label className="chip text-[#A3A3A3]">Naam*</label><input value={name} onChange={(e) => setName(e.target.value)} className="editorial-input dark w-full" data-testid="jace-group-name"/></div>
          <div><label className="chip text-[#A3A3A3]">Omschrijving</label><textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="editorial-input dark w-full" data-testid="jace-group-description"/></div>
          <div>
            <label className="chip text-[#A3A3A3]">Atleten in deze groep ({selected.length})</label>
            <div className="mt-2 border border-[#F5F3F0]/10 max-h-56 overflow-y-auto" data-testid="jace-group-athlete-picker">
              {athletes.length === 0 && <div className="chip text-[#4A4A4A] p-4">Nog geen atleten — maak eerst atleten aan onder ATHLETES.</div>}
              {athletes.map((a) => (
                <label key={a.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-[#F5F3F0]/5 cursor-pointer hover:bg-[#F5F3F0]/5" data-testid={`jace-group-athlete-${a.id}`}>
                  <input type="checkbox" checked={selected.includes(a.id)} onChange={() => toggle(a.id)} className="accent-[#C6FF3C]"/>
                  <span className="text-sm">{a.name}</span>
                  {a.email && <span className="chip text-[#4A4A4A]">{a.email}</span>}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="btn-ghost-dark" data-testid="jace-group-form-cancel">ANNULEREN</button>
          <button onClick={submit} disabled={busy} className="chip px-5 py-2.5" style={{ background: JACE, color: "#0A0A0A" }} data-testid="jace-group-form-save">{busy ? "OPSlaan…" : "OPSLAAN"}</button>
        </div>
      </div>
    </div>
  );
}

export default function JaceGroups() {
  const [items, setItems] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [archived, setArchived] = useState(false);
  const [form, setForm] = useState(null); // null | {} | group
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => {
    api.get(`/jace/groups${archived ? "?archived=true" : ""}`).then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  };
  useEffect(load, [archived]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/jace/athletes").then(({ data }) => setAthletes(data.items || [])).catch(() => {}); }, []);

  const toggleArchive = async (item, value) => {
    try {
      await api.post(`/jace/groups/${item.id}/archive`, { archived: value });
      toast.success(value ? "Groep gearchiveerd" : "Groep hersteld");
      load();
    } catch { toast.error("Archiveren mislukt"); }
  };

  const doDelete = async (item) => {
    try {
      await api.delete(`/jace/groups/${item.id}`);
      toast.success("Groep definitief verwijderd");
      setConfirmDelete(null);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail?.message || "Verwijderen mislukt");
      setConfirmDelete(null);
    }
  };

  return (
    <JaceLayout>
      <div data-testid="jace-groups-page">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="section-tag section-tag-dark mb-3">// GROUPS — SAMENSTELLING</div>
            <h2 className="font-display uppercase text-4xl md:text-6xl">Groups.</h2>
            <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl">Groepen met atleten uit je bestand. Een atleet kan in meerdere groepen zitten.</p>
          </div>
          <button onClick={() => setForm({})} className="chip px-4 py-2.5 inline-flex items-center gap-2" style={{ background: JACE, color: "#0A0A0A" }} data-testid="jace-groups-new-btn"><Plus size={13}/> NEW GROUP</button>
        </div>

        <button onClick={() => setArchived(!archived)} className="chip px-3 py-2 border mb-6 transition-colors" style={archived ? { borderColor: JACE, color: JACE } : { borderColor: "#F5F3F033", color: "#A3A3A3" }} data-testid="jace-groups-archived-toggle">
          {archived ? "ARCHIVE ACTIEF" : "TOON ARCHIVE"}
        </button>

        {items === null ? <div className="chip text-[#A3A3A3]">Laden…</div> : items.length === 0 ? (
          <div className="border border-[#F5F3F0]/15 p-10 text-center" data-testid="jace-groups-empty">
            <UsersRound size={26} className="mx-auto" style={{ color: JACE }}/>
            <div className="font-display uppercase text-2xl mt-4">Nog geen groepen.</div>
            <div className="chip text-[#4A4A4A] mt-2">Maak een groep aan en koppel atleten uit je bestand.</div>
          </div>
        ) : (
          <div className="line-t line-dark" data-testid="jace-groups-list">
            {items.map((item) => (
              <div key={item.id} className="line-b line-dark py-4 flex flex-wrap items-center gap-3" data-testid={`jace-group-row-${item.id}`}>
                <button onClick={() => setForm(item)} className="flex-1 min-w-56 text-left">
                  <div className="font-display uppercase text-xl">{item.name}</div>
                  <div className="chip text-[#A3A3A3] mt-1 inline-flex items-center gap-1.5"><Users size={11}/> {(item.athlete_ids || []).length} atleet(en){item.description ? ` · ${item.description.slice(0, 60)}` : ""}</div>
                </button>
                <button onClick={() => setForm(item)} className="chip px-3 py-2 border border-[#F5F3F0]/25 text-[#A3A3A3] hover:text-white" data-testid={`jace-group-edit-${item.id}`}>EDIT</button>
                <button onClick={() => toggleArchive(item, !archived)} className="chip px-3 py-2 border border-[#F5F3F0]/25 text-[#A3A3A3] hover:text-white inline-flex items-center gap-1.5" data-testid={archived ? `jace-group-restore-${item.id}` : `jace-group-archive-${item.id}`}>
                  {archived ? <><RotateCcw size={12}/> HERSTEL</> : <><Archive size={12}/> ARCHIVE</>}
                </button>
                <button onClick={() => setConfirmDelete(item)} className="chip px-3 py-2 border border-[#F87171]/40 text-[#F87171] inline-flex items-center gap-1.5" data-testid={`jace-group-delete-${item.id}`}><Trash2 size={12}/> DELETE</button>
              </div>
            ))}
          </div>
        )}

        {form !== null && (
          <GroupForm group={form.id ? form : null} athletes={athletes} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />
        )}

        {confirmDelete && (
          <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setConfirmDelete(null)}>
            <div className="bg-[#0A0A0A] border border-[#F87171]/40 max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="jace-group-delete-confirm">
              <div className="chip text-[#F87171] mb-3">// DEFINITIEF VERWIJDEREN</div>
              <p className="text-sm text-[#A3A3A3]">Groep <span className="text-[#F5F3F0]">{confirmDelete.name}</span> wordt permanent verwijderd. Dit is alleen mogelijk als er geen gekoppelde sessies of feedback meer zijn.</p>
              <div className="flex justify-end gap-2 mt-6">
                <button onClick={() => setConfirmDelete(null)} className="btn-ghost-dark" data-testid="jace-group-delete-cancel">ANNULEREN</button>
                <button onClick={() => doDelete(confirmDelete)} className="chip px-4 py-2.5 bg-[#F87171] text-[#0A0A0A]" data-testid="jace-group-delete-confirm-btn">VERWIJDER</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </JaceLayout>
  );
}
